import { FormikHelpers } from 'formik';
import { JobSiteFormData } from '@/jobSites/application/types/JobSiteTypes';
import { useCreateJobSite } from '@/jobSites/application/adapters/createJobSite';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '@/common/presentation/SnackbarProvider';
import { useUpdateJobSite } from '@/jobSites/application/adapters/updateJobSite';
import { useFeatureFlag } from '@/common/presentation/hooks';
import { FEATURE_FLAGS } from '@/common/domain/constants/featureFlags';

export function useJobSiteForm(isNew: boolean, onClose?: () => void) {
  const redirectToJobSiteDetailsAfterAddEnabled = useFeatureFlag(FEATURE_FLAGS.JOBSITES_ADD_NEW_SHOW_DETAILS);
  const stayOnTabAfterEditEnabled = useFeatureFlag(FEATURE_FLAGS.JOBSITES_RESOURCE_ASSIGNMENT_STAY_ON_TAB);

  const navigate = useNavigate();
  const { handleCreateJobSite, error: createError } = useCreateJobSite();
  const { handleUpdateJobSite, error: updateError, loading: updateLoading } = useUpdateJobSite();
  const { showSnackbar, showErrorWithDetails } = useSnackbar();

  const handleSubmit = async (values: JobSiteFormData, { setSubmitting, resetForm }: FormikHelpers<JobSiteFormData>) => {
    let redirectToDetailsPage = false;

    try {
      showSnackbar('Info', 'Saving jobSite...', 'info');
      if (isNew) {
        const result = await handleCreateJobSite(values);
        showSnackbar('Success', 'JobSite created successfully', 'success');

        const jobSiteId = result?.data?.id;
        redirectToDetailsPage = Boolean(redirectToJobSiteDetailsAfterAddEnabled && jobSiteId && !onClose);
        if (redirectToDetailsPage) {
          navigate(`/jobSites/${jobSiteId}`);
          resetForm();
          return; // Early return to avoid the navigation logic below
        }
      } else {
        if (!values.id) throw new Error('JobSite ID is missing');
        await handleUpdateJobSite(values);
        showSnackbar('Success', 'JobSite updated successfully', 'success');
      }

      if (onClose) {
        onClose();
      } else if (!stayOnTabAfterEditEnabled || (isNew && !redirectToDetailsPage)) {
        navigate(-1);
      }
      resetForm();
    } catch (error: unknown) {
      showErrorWithDetails(error, 'Failed to save jobSite', 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  return { handleSubmit, createError, updateError, updateLoading };
}
